import { useState } from "react";
import { Link } from "react-router-dom";
import { Building2, LandPlot, Database, Globe2 } from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "./motion";
import { SectionHead } from "./ui-bits";
import { LogoMark } from "./Logo";
import { AUDIENCES } from "../data/services";

const SECTOR_ICONS = [Building2, LandPlot, Database, Globe2];

const SERVICE_LABELS = [
  "Land Survey CAD",
  "Topographic",
  "Boundary & Property",
  "ALTA/NSPS",
  "As-Built",
  "GIS Mapping",
  "GIS Conversion",
  "Mortgage Location",
  "Subdivision Plat",
  "Easement",
  "Construction Staking",
  "Utility Mapping",
  "Route & ROW",
  "Drone & Point Cloud",
  "FEMA Elevation",
  "Cadastral Mapping",
  "Plot Plans",
  "Grading & Earthwork",
  "Road & Highway",
  "PDF to CAD",
  "Point Cloud to CAD",
  "Data Processing",
];

const SERVICE_SLUGS = [
  "land-survey-cad-drafting",
  "topographic-survey-drafting",
  "boundary-survey-drafting",
  "alta-nsps-survey-drafting",
  "as-built-survey-drafting",
  "gis-mapping-services",
  "gis-data-conversion",
  "mortgage-location-survey-drafting",
  "subdivision-plat-drafting",
  "easement-survey-drafting",
  "construction-staking-drafting",
  "utility-survey-mapping",
  "route-right-of-way-drafting",
  "drone-point-cloud-drafting",
  "fema-elevation-certificate-drafting",
  "cadastral-mapping-services",
  "plot-plan-drafting",
  "grading-earthwork-drafting",
  "road-highway-survey-drafting",
  "pdf-to-cad-conversion",
  "point-cloud-to-cad-conversion",
  "survey-data-processing",
];

const SERVICE_NODES = SERVICE_LABELS.map((label, i) => ({ label, to: `/${SERVICE_SLUGS[i]}` }));

const SOFTWARE = ["AutoCAD Civil 3D", "Carlson Survey", "MicroStation", "ArcGIS", "QGIS", "LandXML", "Google Earth Pro"];

function OrbitRing({ radius, duration, reverse, items, paused }) {
  const dense = items.length > 16;
  return (
    <div
      className="absolute inset-0 orbit-ring"
      style={{
        animationDuration: `${duration}s`,
        animationDirection: reverse ? "reverse" : "normal",
        animationPlayState: paused ? "paused" : "running",
      }}
    >
      <div className="absolute rounded-full border border-dashed border-aqua/25" style={{ inset: `${50 - radius}%` }} aria-hidden="true" />
      {items.map((item, i) => {
        const { label, to } = typeof item === "string" ? { label: item, to: null } : item;
        const angle = (i / items.length) * 360 - 90;
        const rad = (angle * Math.PI) / 180;
        const x = 50 + radius * Math.cos(rad);
        const y = 50 + radius * Math.sin(rad);
        const chip = (
          <span
            className={`orbit-node-inner inline-flex items-center bg-navy border border-navy-light rounded-sm whitespace-nowrap shadow-[0_4px_14px_rgba(11,29,47,0.5)] ${
              dense
                ? "gap-1 px-1.5 py-0.5 font-mono text-[7px] sm:text-[8px] uppercase tracking-wider text-white/75"
                : "gap-1.5 px-2 py-1 font-mono text-[8px] sm:text-[9px] uppercase tracking-wider text-white/75"
            }${to ? " hover:border-aqua/60 hover:text-white transition-colors cursor-pointer" : ""}`}
            style={{
              animationDuration: `${duration}s`,
              animationDirection: reverse ? "normal" : "reverse",
              animationPlayState: paused ? "paused" : "running",
            }}
          >
            <span className="h-1 w-1 rotate-45 bg-aqua shrink-0" aria-hidden="true" />
            {label}
          </span>
        );
        return (
          <div key={label} className="absolute" style={{ left: `${x}%`, top: `${y}%`, transform: "translate(-50%, -50%)" }}>
            {to ? (
              <Link to={to} data-testid={`orbit-node-${to.slice(1)}`} aria-label={`View ${label} service`}>
                {chip}
              </Link>
            ) : (
              chip
            )}
          </div>
        );
      })}
    </div>
  );
}

function OrbitDiagram() {
  const [paused, setPaused] = useState(false);
  return (
    <div data-testid="orbit-diagram">
      <div className="relative aspect-square w-full max-w-[600px] mx-auto bg-navy-deep rounded-md border border-navy-light overflow-hidden">
        <div className="absolute inset-0 bg-contours-dark opacity-50" aria-hidden="true" />
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-64 w-64 rounded-full bg-aqua/10 blur-3xl" aria-hidden="true" />

        <button
          onClick={() => setPaused(!paused)}
          data-testid="orbit-pause-toggle"
          aria-pressed={paused}
          className="absolute top-4 right-4 z-20 inline-flex items-center gap-2.5 group"
        >
          <span className={`relative h-4 w-8 rounded-full transition-colors ${paused ? "bg-navy-light" : "bg-aqua"}`}>
            <span className={`absolute top-0.5 h-3 w-3 rounded-full bg-navy-deep transition-all ${paused ? "left-0.5" : "left-[18px]"}`} />
          </span>
          <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/60 group-hover:text-white transition-colors">
            {paused ? "Resume Motion" : "Pause Motion"}
          </span>
        </button>

        <OrbitRing radius={41} duration={95} reverse={false} items={SERVICE_NODES} paused={paused} />
        <OrbitRing radius={29} duration={65} reverse={true} items={SOFTWARE} paused={paused} />
        <OrbitRing radius={17} duration={42} reverse={false} items={AUDIENCES.map((a) => a.title)} paused={paused} />

        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center z-10">
          <div className="h-20 w-20 sm:h-24 sm:w-24 rounded-full bg-navy border border-aqua/40 flex items-center justify-center shadow-[0_0_60px_rgba(32,196,199,0.3)]">
            <LogoMark className="h-10 w-10 sm:h-12 sm:w-12" />
          </div>
          <span className="mt-3 font-mono text-[9px] uppercase tracking-[0.3em] text-aqua">GeoDrafts Studio</span>
        </div>
      </div>

      <div className="mt-5 space-y-2.5" data-testid="orbit-legend">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-aqua w-28 shrink-0">Sectors</span>
          {AUDIENCES.map((a) => (
            <span key={a.title} className="font-mono text-[9px] uppercase tracking-wider text-white/55 border border-navy-light rounded-sm px-2 py-1">{a.title}</span>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-aqua w-28 shrink-0">Software</span>
          {SOFTWARE.slice(0, 5).map((s) => (
            <span key={s} className="font-mono text-[9px] uppercase tracking-wider text-white/55 border border-navy-light rounded-sm px-2 py-1">{s}</span>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-aqua w-28 shrink-0">Survey Services</span>
          {SERVICE_NODES.slice(0, 8).map((s) => (
            <Link
              key={s.label}
              to={s.to}
              data-testid={`orbit-legend-${s.to.slice(1)}`}
              className="font-mono text-[9px] uppercase tracking-wider text-white/55 border border-navy-light rounded-sm px-2 py-1 hover:border-aqua/60 hover:text-white transition-colors"
            >
              {s.label}
            </Link>
          ))}
          <Link to="/services" data-testid="orbit-legend-all-services" className="font-mono text-[9px] uppercase tracking-wider text-aqua/70 px-1 hover:text-aqua transition-colors">+{SERVICE_NODES.length - 8} more</Link>
        </div>
      </div>
    </div>
  );
}

export default function OrbitSection() {
  return (
    <section className="bg-navy-deep py-20 lg:py-28 relative overflow-hidden" data-testid="orbit-section">
      <div className="absolute inset-0 bg-contours-dark opacity-40" aria-hidden="true" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <SectionHead
            dark
            eyebrow="Sectors We Support"
            title="One Studio. Every Survey Discipline."
            lead="Our production coverage orbits a single standard: your data in, checked deliverables out — across every sector and survey type we serve."
          />
          <Stagger className="grid sm:grid-cols-2 gap-5 mt-12">
            {AUDIENCES.map((a, i) => {
              const Icon = SECTOR_ICONS[i];
              return (
                <StaggerItem key={a.title} className="h-full">
                  <div className="h-full bg-navy border border-navy-light rounded-md p-6 hover:border-aqua/40 transition-colors" data-testid="orbit-sector-card">
                    <Icon className="h-5 w-5 text-aqua" strokeWidth={1.75} />
                    <h3 className="mt-3 font-display font-bold text-white leading-snug">{a.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-white/60">{a.desc}</p>
                  </div>
                </StaggerItem>
              );
            })}
          </Stagger>
        </div>
        <Reveal delay={0.15}>
          <OrbitDiagram />
        </Reveal>
      </div>
    </section>
  );
}
